import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CalendarRange, TicketPlus, ListChecks, LogOut, Menu, Bus } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import ErrorBoundary from "@/components/ErrorBoundary";
import api from "@/lib/api";

const NAV = [
  { to: "/portal", label: "Disponibilidad", icon: CalendarRange, end: true, testid: "portal-nav-disponibilidad" },
  { to: "/portal/nueva", label: "Nueva reserva", icon: TicketPlus, testid: "portal-nav-nueva" },
  { to: "/portal/reservas", label: "Mis reservas", icon: ListChecks, testid: "portal-nav-reservas" },
];

function SidebarContent({ onNavigate, agencia }) {
  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-200">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-indigo-600 text-white">
          <Bus className="h-5 w-5" />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-bold text-slate-900 text-sm">Portal de Agencias</div>
          <div className="text-[11px] text-slate-500 tracking-wide truncate max-w-[150px]">{agencia || "Tours NY"}</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-indigo-50 text-indigo-700 font-semibold"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px] shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="px-5 py-4 border-t border-slate-200">
        <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-slate-400">Tours NY · Reservas</p>
      </div>
    </div>
  );
}

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const agencia = user?.agencia_nombre;
  // PROMPT 117: desde que fecha se reserva por el Portal (lo define Tours NY en Configuracion).
  const [reservasDesde, setReservasDesde] = useState(null);
  useEffect(() => {
    api.get("/portal/perfil").then(({ data }) => setReservasDesde(data?.reservas_desde || null)).catch(() => {});
  }, []);
  const hoy = new Date();
  const hoyISO = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`;
  const avisoDesde = reservasDesde && reservasDesde > hoyISO ? reservasDesde.split("-").reverse().join("/") : null;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const currentTitle = NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))?.label || "Portal";

  return (
    <div className="min-h-screen bg-background flex" data-testid="portal-layout">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-slate-200 fixed inset-y-0 left-0 z-30">
        <SidebarContent agencia={agencia} />
      </aside>

      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-16 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="portal-mobile-trigger">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64">
                <SidebarContent onNavigate={() => setMobileOpen(false)} agencia={agencia} />
              </SheetContent>
            </Sheet>
            <h1 className="font-heading text-lg font-semibold text-slate-900">{currentTitle}</h1>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium text-slate-800" data-testid="portal-user-name">{user?.nombre}</span>
              <span className="text-[11px] uppercase tracking-wide text-indigo-600 font-semibold">Agencia</span>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-600 text-white text-sm font-semibold">
              {(agencia || user?.nombre || "?").charAt(0).toUpperCase()}
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="portal-logout-button" className="gap-2">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <div className="max-w-6xl mx-auto">
            {avisoDesde && (
              <div
                className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                data-testid="portal-aviso-reservas-desde"
              >
                Por este portal se reserva a partir del <span className="font-semibold">{avisoDesde}</span>. Para
                fechas anteriores, seguí reservando con Tours NY como hasta ahora.
              </div>
            )}
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
